
import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { Snackbar } from '@mui/material';
import { Alert } from '@mui/material';
import { useGetCountries } from 'hooks/useLookups';
import { useRequestDemo } from 'hooks/useUtility';
import { snackbarService } from '../../../services/snackbarService';

const RequestDemo = ({ planType }: any) => {
  let contactSchema = yup.object().shape({
    email: yup
      .string()
      .trim()
      .email('Must use valid email address')
      .required('Email is required'),
    first_name: yup.string().required('First name is required'),
    last_name: yup.string().required('First name is required'),
    business_name: yup.string().required('Business/Institution is required'),
    message: yup.string().required('Message is required')
  });

  const { mutate: requestMutation } = useRequestDemo(
    (res: any) => {
      const { data, status_code: statusCode } = res;

      if (statusCode == 200) {
        snackbarService.showSnackbar(`Thank you for requesting a demo! We'll contact you within 24 hours to set up your account and discuss your needs.`, 'success');
        setFormValues({
          first_name: '',
          last_name: '',
          email: '',
          business_name: '',
          message: '',
          agreed: false,
        });
      } else {
        snackbarService.showSnackbar(`${data.message}`, 'error');
      }
    },
    (error: any) => {
      console.log('error', error);
    },
  )

  const { register, handleSubmit, reset, control } = useForm({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      business_name: '',
      message: '',
    },
    resolver: yupResolver(contactSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit'
  });

  const [formValues, setFormValues] = useState<any>({
    first_name: '',
    last_name: '',
    email: '',
    business_name: '',
    message: '',
    agreed: false
  });

  const { data: countries } = useGetCountries()

  const isValidEmail = (email: string) => {
    const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    return emailPattern.test(email);
  };

  const validateForm = () => {
    if (formValues.first_name === '') {
      snackbarService.showSnackbar('First name is required', 'error');
      return false;
    } else if (formValues.last_name === '') {
      snackbarService.showSnackbar('Last name is required', 'error');
      return false;
    } else if (formValues.email === '') {
      snackbarService.showSnackbar('Email is required', 'error');
      return false;
    } else if (!isValidEmail(formValues.email)) {
      snackbarService.showSnackbar('Invalid email address', 'error');
      return false;
    } else if (formValues.business_name === '') {
      snackbarService.showSnackbar('Business/Institution name is required', 'error');
      return false;
    } else if (formValues.message === '') {
      snackbarService.showSnackbar('Message is required', 'error');
      return false;
    } else if (formValues.agreed === false) {
      snackbarService.showSnackbar('Terms and Conditions must be accepted', 'error');
      return false;
    }
    return true;
  }

  const onSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    const payload = {
      first_name: formValues.first_name,
      last_name: formValues.last_name,
      email: formValues.email,
      plan: planType,
      business_name: formValues.business_name,
      message: formValues.message,
    };

    requestMutation(payload)

  };

  useEffect(() => { }, [countries]);

  const onSubmitHandler = handleSubmit(onSubmit);

  const handleValueChange = (e: any) => {
    const newValue = e.target.type === 'checkbox' ? e.target.checked : e.target.value;

    setFormValues((prevFormValues: any) => ({
      ...prevFormValues,
      [e.target.name]: newValue,
    }));
  };


  return (
    <div>
      <section>
        <div className="row" style={{ marginTop: -60, marginBottom: 30 }}>
          <div className="col-12">
            <div className="planHeading">
              <h1 style={{ marginTop: 30 }}>Request A Demo</h1>
            </div>
          </div>
        </div>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="contactForm" onChange={handleValueChange}>
                <form onSubmit={onSubmitHandler}>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="firstName">First Name <sup>*</sup></label>
                        <input name="first_name" value={formValues.first_name} type="text" id="firstName" className="form-control" placeholder="First Name" required></input>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="lastName">Last Name <sup>*</sup></label>
                        <input name="last_name" value={formValues.last_name} type="text" id="lastName" className="form-control" placeholder="Last Name" required></input>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="emailAddress">Email Address <sup>*</sup></label>
                        <input name="email" value={formValues.email} type="email" id="emailAddress" className="form-control" placeholder="Your Email" required></input>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="business_name">Business/Institution Name <sup>*</sup></label>
                        <input name="business_name" value={formValues.business_name} type="text" id="business_name" className="form-control" placeholder="Business/Institution Name" required></input>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12">
                      <div className="form-group">
                        <label htmlFor="message">Message <sup>*</sup></label>
                        <textarea name="message" value={formValues.message} id="message" className="form-control" placeholder="Your Message..." cols={30} rows={8} required></textarea>
                      </div>
                    </div>
                  </div>
                  <div className="formSubmission d-md-flex justify-content-between align-items-center">
                    <div className="form-group mb-md-0">
                      <input name="agreed" checked={formValues.agreed} type="checkbox" id="conditions"></input>
                      <label htmlFor="conditions">Agree on our <a href="/terms" target="_blank">terms and condition</a> for using your submitted data?</label>
                    </div>
                    <div className="form-group mb-0 text-md-left text-center">
                      <button type="submit" onClick={onSubmit} className='form-control bgBlue login-btn pointer' style={{ width: '120px' }}>Submit</button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

  );
};

export default RequestDemo;
